import { IconButton, cn } from '@nexo/ui';
import {
  ArrowUpRight,
  FolderOpen,
  ListTodo,
  MessageSquareText,
  PanelRightClose,
  PanelRightOpen,
  Pin,
  Users,
  Webhook,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { PinsPanel } from '../features/chat/PinsPanel';
import { ThreadPanel } from '../features/chat/ThreadPanel';
import { FilesPanel } from '../features/files/FilesPanel';
import { IntegrationsPanel } from '../features/integrations/IntegrationsPanel';
import { TaskPanel } from '../features/tasks/TaskPanel';
import { MembersPanel } from '../features/teams/MembersPanel';
import { useTeam, type RightPanel } from './TeamContext';

const panels: { id: RightPanel; label: string; icon: ReactNode }[] = [
  { id: 'members', label: 'Miembros', icon: <Users /> },
  { id: 'tasks', label: 'Tareas', icon: <ListTodo /> },
  { id: 'files', label: 'Archivos', icon: <FolderOpen /> },
  { id: 'integrations', label: 'Integraciones', icon: <Webhook /> },
];

/** Panels opened from a conversation rather than from the rail. */
const contextualPanels: { id: RightPanel; label: string; icon: ReactNode }[] = [
  { id: 'thread', label: 'Hilo', icon: <MessageSquareText /> },
  { id: 'pins', label: 'Mensajes fijados', icon: <Pin /> },
];

/** Thin vertical bar on the far right that switches the contextual panel. */
export function UtilityRail() {
  const { t } = useTranslation();
  const { rightPanel, setRightPanel } = useTeam();
  return (
    <nav
      aria-label={t('Paneles')}
      className="hidden w-11 shrink-0 flex-col items-center gap-1 pt-1 md:flex"
    >
      {panels.map((panel) => (
        <IconButton
          key={panel.id}
          label={t(panel.label)}
          active={rightPanel === panel.id}
          onClick={() => setRightPanel(rightPanel === panel.id ? null : panel.id)}
          className={cn(rightPanel !== panel.id && 'hover:bg-surface')}
        >
          {panel.icon}
        </IconButton>
      ))}
      <span className="flex-1" />
      <IconButton
        label={rightPanel ? t('Ocultar panel') : t('Mostrar panel')}
        onClick={() => setRightPanel(rightPanel ? null : 'members')}
        className="hover:bg-surface"
      >
        {rightPanel ? <PanelRightClose /> : <PanelRightOpen />}
      </IconButton>
    </nav>
  );
}

export function RightPanelView({ panel }: { panel: RightPanel }) {
  const { t } = useTranslation();
  const { team, setRightPanel } = useTeam();
  const { channelId } = useParams();
  const current = [...panels, ...contextualPanels].find((p) => p.id === panel);

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <span className="text-accent [&_svg]:size-[18px]">{current?.icon}</span>
        <h2 className="flex-1 text-[15px] font-semibold">{current && t(current.label)}</h2>
        {panel === 'tasks' && (
          <Link
            to={`/app/t/${team.id}/tasks`}
            title={t('Abrir todas las tareas')}
            aria-label={t('Abrir todas las tareas')}
            className="inline-flex size-7 items-center justify-center rounded-lg text-muted hover:bg-surface-2 hover:text-text"
          >
            <ArrowUpRight className="size-4" />
          </Link>
        )}
        <IconButton label={t('Cerrar panel')} size="sm" onClick={() => setRightPanel(null)}>
          <X />
        </IconButton>
      </header>
      {/* On small screens (no utility rail) the panels are switched here. */}
      <div className="flex gap-1 border-b border-border px-3 py-2 md:hidden">
        {panels.map((p) => (
          <IconButton
            key={p.id}
            label={t(p.label)}
            size="sm"
            active={p.id === panel}
            onClick={() => setRightPanel(p.id)}
          >
            {p.icon}
          </IconButton>
        ))}
      </div>
      {panel === 'members' && <MembersPanel />}
      {panel === 'tasks' && <TaskPanel />}
      {panel === 'files' && <FilesPanel channelId={channelId} />}
      {panel === 'integrations' && <IntegrationsPanel channelId={channelId} />}
      {panel === 'thread' && <ThreadPanel />}
      {panel === 'pins' && <PinsPanel channelId={channelId} />}
    </div>
  );
}
